/**
 * 经脉系统数据
 * 奇经八脉 × 穴位
 */

export interface Acupoint {
  name: string;
  hpBonus?: number;
  mpBonus?: number;
  attackBonus?: number;
  defenseBonus?: number;
  expBonus?: number;       // 修炼速度加成（百分比）
  stoneBonus?: number;     // 灵石掉落加成
  breakthroughBonus?: number;
  craftBonus?: number;
}

export interface Meridian {
  id: string;
  name: string;
  icon: string;
  desc: string;
  /** 穴位列表 */
  points: Acupoint[];
  /** 基础灵石消耗 */
  baseStoneCost: number;
  /** 难度系数 */
  difficulty: number;
  /** 颜色 */
  color: string;
  /** 贯通效果描述 */
  masteryDesc: string;
  /** 贯通效果（仅在全部穴位冲开后触发） */
  masteryBonus: Partial<Acupoint> & { special?: string };
}

export const MERIDIANS: Meridian[] = [
  {
    id: 'ren',
    name: '任脉',
    icon: '🔴',
    desc: '行于腹面正中，主一身之阴气。贯通后可大幅提升生命力和恢复能力。',
    color: '#e55',
    baseStoneCost: 200,
    difficulty: 1,
    masteryDesc: 'HP上限+15%，每秒恢复HP+1',
    masteryBonus: { hpBonus: 0.15, special: 'hp_regen' },
    points: [
      { name: '会阴', hpBonus: 20 },
      { name: '关元', hpBonus: 25 },
      { name: '气海', hpBonus: 30 },
      { name: '神阙', hpBonus: 25, defenseBonus: 5 },
      { name: '中脘', hpBonus: 30 },
      { name: '膻中', hpBonus: 35, defenseBonus: 5 },
      { name: '天突', hpBonus: 30 },
      { name: '廉泉', hpBonus: 35 },
      { name: '承浆', hpBonus: 50, defenseBonus: 10 },
    ],
  },
  {
    id: 'du',
    name: '督脉',
    icon: '🔵',
    desc: '行于背面正中，主一身之阳气。贯通后防御大增，危急时刻可保命。',
    color: '#48f',
    baseStoneCost: 200,
    difficulty: 1,
    masteryDesc: '防御+15%，战斗首回合减伤',
    masteryBonus: { defenseBonus: 0.15, special: 'first_round_shield' },
    points: [
      { name: '长强', defenseBonus: 5 },
      { name: '腰俞', defenseBonus: 5, hpBonus: 10 },
      { name: '命门', defenseBonus: 8 },
      { name: '至阳', defenseBonus: 8, hpBonus: 15 },
      { name: '大椎', defenseBonus: 10 },
      { name: '风府', defenseBonus: 10, hpBonus: 15 },
      { name: '百会', defenseBonus: 12 },
      { name: '神庭', defenseBonus: 12, hpBonus: 20 },
      { name: '人中', defenseBonus: 15, hpBonus: 25 },
    ],
  },
  {
    id: 'chong',
    name: '冲脉',
    icon: '🟢',
    desc: '又称血海，主一身之气血。贯通后攻击力暴增，有概率造成暴击。',
    color: '#6b8',
    baseStoneCost: 350,
    difficulty: 2,
    masteryDesc: '攻击+15%，暴击率+10%',
    masteryBonus: { attackBonus: 0.15, special: 'crit_chance' },
    points: [
      { name: '气冲', attackBonus: 5 },
      { name: '阴交', attackBonus: 5 },
      { name: '石门', attackBonus: 8 },
      { name: '四满', attackBonus: 10 },
      { name: '大赫', attackBonus: 10 },
      { name: '横骨', attackBonus: 12 },
      { name: '血海', attackBonus: 15, hpBonus: 30 },
    ],
  },
  {
    id: 'dai',
    name: '带脉',
    icon: '🟡',
    desc: '环绕腰间，约束诸经。贯通后灵力充沛，修炼消耗大减。',
    color: '#fb3',
    baseStoneCost: 350,
    difficulty: 2,
    masteryDesc: 'MP上限+15%，运功甜蜜区+20%',
    masteryBonus: { mpBonus: 0.15, special: 'breathing_boost' },
    points: [
      { name: '带脉穴', mpBonus: 15 },
      { name: '五枢', mpBonus: 15 },
      { name: '维道', mpBonus: 20 },
      { name: '足临泣', mpBonus: 20 },
      { name: '地五会', mpBonus: 25 },
      { name: '侠溪', mpBonus: 25 },
      { name: '足窍阴', mpBonus: 40 },
    ],
  },
  {
    id: 'yinqiao',
    name: '阴跷脉',
    icon: '🟣',
    desc: '行于下肢内侧，主静。贯通后修炼速度显著提升。',
    color: '#a6f',
    baseStoneCost: 600,
    difficulty: 3,
    masteryDesc: '修炼速度+15%',
    masteryBonus: { expBonus: 0.15 },
    points: [
      { name: '照海', expBonus: 0.03 },
      { name: '交信', expBonus: 0.03 },
      { name: '阴谷', expBonus: 0.04 },
      { name: '横骨', expBonus: 0.04 },
      { name: '幽门', expBonus: 0.06 },
    ],
  },
  {
    id: 'yangqiao',
    name: '阳跷脉',
    icon: '🟠',
    desc: '行于下肢外侧，主动。贯通后战斗收益大增。',
    color: '#f90',
    baseStoneCost: 600,
    difficulty: 3,
    masteryDesc: '战斗灵石掉落+30%',
    masteryBonus: { stoneBonus: 0.30 },
    points: [
      { name: '申脉', stoneBonus: 0.05 },
      { name: '仆参', stoneBonus: 0.05 },
      { name: '跗阳', stoneBonus: 0.08 },
      { name: '居髎', stoneBonus: 0.08 },
      { name: '臑俞', stoneBonus: 0.10 },
    ],
  },
  {
    id: 'yinwei',
    name: '阴维脉',
    icon: '⚪',
    desc: '维系诸阴经，与任脉交汇。贯通后大境界突破事半功倍。',
    color: '#ccc',
    baseStoneCost: 1000,
    difficulty: 4,
    masteryDesc: '大境界突破+10%成功率（一次性消耗，用后需重冲）',
    masteryBonus: { breakthroughBonus: 0.10, special: 'one_time_breakthrough' },
    points: [
      { name: '筑宾', breakthroughBonus: 0.03 },
      { name: '府舍', breakthroughBonus: 0.03 },
      { name: '大横', breakthroughBonus: 0.04 },
    ],
  },
  {
    id: 'yangwei',
    name: '阳维脉',
    icon: '⚫',
    desc: '维系诸阳经，与督脉交汇。贯通后炼丹炼器如有神助。',
    color: '#999',
    baseStoneCost: 1000,
    difficulty: 4,
    masteryDesc: '炼丹炼器成功率+5%，10%概率不消耗材料',
    masteryBonus: { craftBonus: 0.05, special: 'free_craft' },
    points: [
      { name: '金门', craftBonus: 0.02 },
      { name: '阳交', craftBonus: 0.02 },
      { name: '风池', craftBonus: 0.03 },
    ],
  },
];

/** 获取某经脉的总穴位数 */
export function getTotalPoints(meridianId: string): number {
  const m = MERIDIANS.find((m) => m.id === meridianId);
  return m ? m.points.length : 0;
}

/** 已通总穴位数 */
export function getTotalOpened(meridians: Record<string, number>): number {
  let total = 0;
  for (const m of MERIDIANS) {
    total += meridians[m.id] ?? 0;
  }
  return total;
}

/** 经脉总穴位数 */
export const TOTAL_POINTS = MERIDIANS.reduce((sum, m) => sum + m.points.length, 0);
